<template>
  <div v-if="isViewable">
    <BaseButton
      :label="buttonLabel"
      variant="primary"
      size="md"
      :loading="isLoading"
      :disabled="isDisabled"
      @click="handlePublish"
      :tooltip="buttonTooltip"
    />

    <ConfirmationModal
      :visible="isConfirmationModalOpen"
      title="Are you sure?"
      message="Do you want to publish menu changes?"
      @confirm="onConfirmationClicked"
      @update:visible="isConfirmationModalOpen = $event" />
  </div>
</template>

<script>
import { Menu as MenuApi } from '@/services/SOLO';
import BaseButton from '../BaseButton.vue';
import moment from 'moment';
import ConfirmationModal from '../Modals/ConfirmationModal.vue';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Feature_Flags } from '@/enums/globals';

export default {
  name: 'PublishMenuButton',
  components: {
    BaseButton,
    ConfirmationModal,
  },
  data () {
    return {
      isLoading: false,
      disabled: false,
      lastPublished: null,
      isConfirmationModalOpen: false,
      isViewable: false,
      buttonTooltip: '',
    };
  },
  async mounted () {
    const { isFeatureEnabled } = useFeatureFlags();
    this.isViewable = isFeatureEnabled(Feature_Flags.PUBLISH_MENU_OPTIMIZATION_BUTTON);
    if (this.isViewable) {
      this.checkPublishStatus();
    }
  },
  computed: {
    isDisabled () {
      return this.disabled || this.isLoading;
    },
    buttonLabel () {
      const baseLabel = 'General Publish';

      if (!this.lastPublished) {
        return baseLabel;
      }

      const lastPublishedMoment = moment(this.lastPublished).locale('en-us');
      const now = moment().locale('en-us');
      const diffInHours = now.diff(lastPublishedMoment, 'hours');

      let timeDisplay = '';

      if (diffInHours < 24) {
        // Within 24 hours - show relative time
        timeDisplay = lastPublishedMoment.fromNow();
      } else {
        // More than 24 hours - show formatted date and time
        timeDisplay = lastPublishedMoment.format('DD/MM/YYYY h:mm A');
      }

      return `${baseLabel} (${timeDisplay})`;
    },
  },
  watch: {
    '$route.params.id': function () {
      if (this.isViewable) {
        this.checkPublishStatus();
      }
    },
  },
  methods: {
    handlePublish () {
      this.isConfirmationModalOpen = true;
    },
    async checkPublishStatus () {
      this.isLoading = true;
      try {
        const menuId = this.$route.params.id;
        const { data } = await MenuApi.checkPublishMenuStatus(menuId);
        if (data?.data) {
          this.lastPublished = data?.data?.finished_at;
          this.disabled = data?.data?.status === 'pending';
          this.buttonTooltip = data?.data?.status === 'pending' ? 'Publishing in progress..' : '';
        } else {
          this.lastPublished = null;
          this.disabled = false;
          this.buttonTooltip = '';
        }
      } catch (e) {
        console.error('Failed to fetch menu status:', e);
      } finally {
        this.isLoading = false;
      }
    },
    async onConfirmationClicked () {
      this.isLoading = true;
      try {
        const menuId = this.$route.params.id;
        const { data } = await MenuApi.publishMenu(menuId);
        this.toastAMessage('In progress', 'Your menu changes is being published.', 'info');
        this.checkPublishStatus();
      } catch (e) {
        if (e?.response?.data?.error?.code === 'MENU_PUBLISH_CONFLICT') {
          this.toastAMessage('Hold on!', e?.response?.data?.error?.detail, 'warning');
        } else {
          this.toastAMessage('Error!', e?.response?.data?.error?.detail || 'Something Went wrong!', 'danger');
        }
      } finally {
        this.isLoading = false;
      }
    },
    toastAMessage (title, message, type) {
      const typesIcons = {
        success: 'fas fa-check',
        danger: 'fas fa bomb',
        warning: 'fas fa-info-circle',
        info: 'fas fa-spinner fa-spin',
      };
      this.$notify({
        title: title,
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: message,
        type,
        icon: typesIcons[type],
      });
    },
  },
};
</script>
